import { z } from 'zod';
import { dataHoraSchema, dataSchema, respostaListaSchema, uuidSchema } from './comum.ts';
import { TAMANHO_MAXIMO_EMAIL } from './contato.ts';

/**
 * Envio do pedido de compra ao fornecedor por e-mail (`.spec/015`, decisão 114).
 *
 * **O e-mail sai pela Mensageria, e não pelo SMTP do sistema.** O `SMTP_*` da
 * API manda convite e troca de senha em nome do ObraNaLinha, e ninguém responde
 * àquilo. O pedido é o contrário: o fornecedor responde — confirma prazo,
 * avisa que falta material —, e a resposta tem de cair na caixa **da
 * construtora**. A Mensageria manda pela conta SMTP cadastrada na chave dela, e
 * a chave é da conta (`configuracao_conta.mensageria_chave_api`).
 *
 * **O PDF vem do navegador.** O impresso é montado só na tela (decisão 54), e
 * uma biblioteca de documento na API seria o segundo código a desenhar o mesmo
 * papel. A tela gera o arquivo com a mesma função do botão "PDF" e o manda em
 * base64 no payload — o caminho do logotipo da empresa (decisão 53). O servidor
 * confere que é PDF e o tamanho, e não guarda o arquivo: ele fica na fila da
 * Mensageria até o expurgo dela. O que fica aqui é o fato — para quem, quando,
 * com que assunto e texto, e o que a Mensageria respondeu.
 *
 * **Um destinatário, e as cópias em `cc`.** A Mensageria transforma cada item
 * de `to` numa mensagem separada, com id próprio; o pedido mandado a três
 * endereços viraria três envios para acompanhar. Com `cc`, é um e-mail — o que
 * o fornecedor recebe e responde a todos.
 */

/* -------------------------------------------------------------------------- */
/* Códigos e rótulos                                                          */
/* -------------------------------------------------------------------------- */

/**
 * O que a Mensageria disse da mensagem, em quatro estados.
 *
 * Ela tem mais (`sending`, `delivered`, `read`), e os dois últimos dependem de
 * webhook do provedor — o próprio guia da integração manda não contar com eles
 * para regra. `NA_FILA` cobre `queued` e `sending`; `ENVIADO` cobre `sent` e o
 * que vier depois dele.
 */
export const SITUACOES_ENVIO = ['NA_FILA', 'ENVIADO', 'FALHOU', 'CANCELADO'] as const;
export const situacaoEnvioSchema = z.enum(SITUACOES_ENVIO);
export type SituacaoEnvio = z.infer<typeof situacaoEnvioSchema>;

export const ROTULO_SITUACAO_ENVIO: Readonly<Record<SituacaoEnvio, string>> = {
  NA_FILA: 'Na fila',
  ENVIADO: 'Enviado',
  FALHOU: 'Falhou',
  CANCELADO: 'Cancelado',
};

/** Estados que ainda podem mudar — os únicos que a leitura vai perguntar à Mensageria. */
export function envioEmAndamento(situacao: SituacaoEnvio): boolean {
  return situacao === 'NA_FILA';
}

/**
 * Tradução do `status` da Mensageria. Status desconhecido fica `NA_FILA`: a
 * leitura seguinte pergunta de novo, e é melhor do que declarar enviado o que
 * ninguém sabe.
 */
export function situacaoDaMensageria(status: string): SituacaoEnvio {
  switch (status) {
    case 'sent':
    case 'delivered':
    case 'read':
      return 'ENVIADO';
    case 'failed':
      return 'FALHOU';
    case 'cancelled':
      return 'CANCELADO';
    default:
      return 'NA_FILA';
  }
}

/** Teto das cópias. Cinco cobre comprador, gerente e o vendedor do fornecedor. */
export const MAXIMO_COPIAS_ENVIO = 5;
/** O teto do `subject` na Mensageria. */
export const TAMANHO_MAXIMO_ASSUNTO_ENVIO = 300;
/**
 * O texto do e-mail. A Mensageria aceita 20.000 no corpo, que vai em HTML; o
 * texto daqui vira parágrafos escapados, e 4.000 deixa folga para a marcação.
 */
export const TAMANHO_MAXIMO_MENSAGEM_ENVIO = 4000;
/**
 * O PDF, em bytes. O impresso de um pedido de trinta itens com logotipo fica
 * abaixo de 300 KB; 3 MB é dez vezes isso, e em base64 (~4 MB) ainda cabe no
 * `bodyLimit` próprio da rota.
 */
export const TAMANHO_MAXIMO_ANEXO_ENVIO = 3 * 1024 * 1024;

/* -------------------------------------------------------------------------- */
/* Payload                                                                    */
/* -------------------------------------------------------------------------- */

const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(TAMANHO_MAXIMO_EMAIL, `O e-mail tem no máximo ${TAMANHO_MAXIMO_EMAIL} caracteres.`)
  .pipe(z.email('E-mail inválido.'));

/** Base64 padrão, sem prefixo `data:` — é o formato do `content` da Mensageria. */
const base64Schema = z
  .string()
  .regex(/^[A-Za-z0-9+/]+={0,2}$/, 'O anexo não está em base64.')
  .refine(
    (valor) => tamanhoDoBase64(valor) <= TAMANHO_MAXIMO_ANEXO_ENVIO,
    `O PDF passa de ${TAMANHO_MAXIMO_ANEXO_ENVIO / 1024 / 1024} MB.`,
  );

/** Quantos bytes o base64 decodifica, sem decodificá-lo. */
export function tamanhoDoBase64(valor: string): number {
  const preenchimento = valor.endsWith('==') ? 2 : valor.endsWith('=') ? 1 : 0;
  return Math.floor((valor.length * 3) / 4) - preenchimento;
}

export const anexoDoEnvioSchema = z.object({
  nomeArquivo: z
    .string()
    .trim()
    .min(1)
    .max(120)
    .regex(/\.pdf$/i, 'O anexo é o PDF do pedido.'),
  conteudoBase64: base64Schema,
});

export type AnexoDoEnvio = z.infer<typeof anexoDoEnvioSchema>;

/**
 * O que a tela manda. **Sem situação, sem id da Mensageria, sem o destinatário
 * "oficial" do fornecedor**: os dois primeiros são resposta da Mensageria, e o
 * e-mail do cadastro é só a sugestão que a tela preenche — o comprador manda
 * para o vendedor que o atende, que nem sempre é o endereço do cadastro.
 */
export const criarEnvioPedidoSchema = z
  .object({
    para: emailSchema,
    cc: z
      .array(emailSchema)
      .max(MAXIMO_COPIAS_ENVIO, `No máximo ${MAXIMO_COPIAS_ENVIO} cópias.`)
      .prefault([]),
    assunto: z.string().trim().min(1, 'Informe o assunto.').max(TAMANHO_MAXIMO_ASSUNTO_ENVIO),
    mensagem: z.string().trim().min(1, 'Escreva a mensagem.').max(TAMANHO_MAXIMO_MENSAGEM_ENVIO),
    anexo: anexoDoEnvioSchema,
  })
  .superRefine((dados, ctx) => {
    // O mesmo endereço em "para" e em "cc" chega duas vezes à mesma caixa.
    dados.cc.forEach((copia, indice) => {
      const repetida = copia === dados.para || dados.cc.indexOf(copia) !== indice;
      if (!repetida) return;
      ctx.addIssue({
        code: 'custom',
        path: ['cc', indice],
        message: `${copia} já está entre os destinatários.`,
      });
    });
  });

export type CriarEnvioPedido = z.infer<typeof criarEnvioPedidoSchema>;

/* -------------------------------------------------------------------------- */
/* Leitura                                                                    */
/* -------------------------------------------------------------------------- */

export const envioPedidoSchema = z.object({
  id: uuidSchema,
  pedidoCompraId: uuidSchema,
  para: z.string(),
  cc: z.array(z.string()),
  assunto: z.string(),
  mensagem: z.string(),
  nomeAnexo: z.string(),
  situacao: situacaoEnvioSchema,
  /** O motivo da falha, como a Mensageria escreveu. */
  erro: z.string().nullable(),
  enviadoEm: dataHoraSchema,
  enviadoPorNome: z.string().nullable(),
});

export type EnvioPedido = z.infer<typeof envioPedidoSchema>;

/**
 * A sugestão do diálogo: para quem o último pedido **deste fornecedor** foi
 * mandado — de qualquer pedido, não só deste —, e, sem envio anterior, o e-mail
 * do cadastro dele, sem cópia.
 *
 * O envio anterior vem **antes** do cadastro (decisão 115): o endereço a que o
 * comprador manda pedido é o do vendedor que o atende, e é isso que o último
 * envio registrou, cópias inclusive. O e-mail do cadastro costuma ser o geral
 * da loja — serve para o primeiro pedido, e o comprador troca se souber o do
 * vendedor.
 */
export const destinatarioSugeridoSchema = z.object({
  para: z.string(),
  cc: z.array(z.string()),
});

export type DestinatarioSugerido = z.infer<typeof destinatarioSugeridoSchema>;

/** A precedência descrita acima, como função: o último envio, senão o cadastro. */
export function sugestaoDoEnvio(
  ultimo: DestinatarioSugerido | null,
  emailDoCadastro: string | null,
): DestinatarioSugerido | null {
  if (ultimo) return ultimo;
  return emailDoCadastro ? { para: emailDoCadastro, cc: [] } : null;
}

/** Do mais novo ao mais antigo: a tela abre respondendo "o último foi?". */
export const enviosDoPedidoSchema = z.object({
  itens: z.array(envioPedidoSchema),
  sugestao: destinatarioSugeridoSchema.nullable(),
});

export type EnviosDoPedido = z.infer<typeof enviosDoPedidoSchema>;

/* -------------------------------------------------------------------------- */
/* Configuração da conta                                                      */
/* -------------------------------------------------------------------------- */

/**
 * O formato das chaves da Mensageria (`msg_` e o resto). A conferência é de
 * forma; se a chave vale, quem diz é o teste de conexão.
 */
export const chaveMensageriaSchema = z
  .string()
  .trim()
  .regex(/^msg_[A-Za-z0-9_-]{8,200}$/, 'A chave da Mensageria começa com msg_.');

/**
 * `null` apaga. Rota própria, e não um campo do `PUT` da conta: aquele regrava
 * a configuração inteira com o que a tela leu, e a chave **não** se lê de
 * volta — a tela teria de mandar a chave que não tem, ou o `PUT` apagaria a
 * chave a cada "Salvar configuração".
 */
export const gravarMensageriaSchema = z.object({
  chave: chaveMensageriaSchema.nullable(),
});

export type GravarMensageria = z.infer<typeof gravarMensageriaSchema>;

/** O que se mostra da chave: se existe, e os quatro últimos caracteres. */
export const mensageriaDaContaSchema = z.object({
  /** O servidor tem `MENSAGERIA_URL`. Sem ele, a chave não serve. */
  servicoConfigurado: z.boolean(),
  chaveConfigurada: z.boolean(),
  finalDaChave: z.string().nullable(),
});

export type MensageriaDaConta = z.infer<typeof mensageriaDaContaSchema>;

export const testeMensageriaSchema = z.object({
  ok: z.boolean(),
  mensagem: z.string(),
});

export type TesteMensageria = z.infer<typeof testeMensageriaSchema>;

/* -------------------------------------------------------------------------- */
/* Envio em lote — a fila de pedidos a enviar (`CMP·10`, decisão 116)          */
/* -------------------------------------------------------------------------- */

/**
 * "Já foi enviado?" — o filtro da fila.
 *
 * **Enviado é ter um envio que vale**: na fila ou enviado. O que falhou ou foi
 * cancelado na Mensageria não conta, e o pedido volta a "não enviado": o
 * fornecedor não recebeu, e a fila é justamente onde se manda de novo.
 */
export const FILTROS_ENVIO = ['NAO_ENVIADOS', 'ENVIADOS', 'TODOS'] as const;
export const filtroEnvioSchema = z.enum(FILTROS_ENVIO);
export type FiltroEnvio = z.infer<typeof filtroEnvioSchema>;

export const ROTULO_FILTRO_ENVIO: Readonly<Record<FiltroEnvio, string>> = {
  NAO_ENVIADOS: 'Não enviados',
  ENVIADOS: 'Enviados',
  TODOS: 'Todos',
};

/** As situações que fazem o pedido contar como enviado. Ver `FILTROS_ENVIO`. */
export const SITUACOES_ENVIO_QUE_VALEM = [
  'NA_FILA',
  'ENVIADO',
] as const satisfies readonly SituacaoEnvio[];

export function envioVale(situacao: SituacaoEnvio): boolean {
  return (SITUACOES_ENVIO_QUE_VALEM as readonly SituacaoEnvio[]).includes(situacao);
}

/**
 * A fila: pedidos **em aberto** (`PENDENTE`), do mais antigo ao mais novo.
 *
 * A situação do pedido não é filtro, e é decisão: a tela é de mandar ao
 * fornecedor o que ele ainda tem de entregar. O atendido e o encerrado se
 * reenviam pela tela do pedido, um a um, que é onde se explica o porquê. O
 * período é a data do pedido, inclusiva nas duas pontas.
 */
export const listarFilaDeEnvioQuerySchema = z
  .object({
    page: z.coerce.number().int().min(1).prefault(1),
    pageSize: z.coerce.number().int().min(1).max(200).prefault(50),
    empresaId: uuidSchema.optional(),
    fornecedorId: uuidSchema.optional(),
    de: dataSchema.optional(),
    ate: dataSchema.optional(),
    envio: filtroEnvioSchema.prefault('NAO_ENVIADOS'),
  })
  .strict();

export type ListarFilaDeEnvioQuery = z.infer<typeof listarFilaDeEnvioQuerySchema>;

/** O último envio do pedido, valendo ou não — é o que diz "falhou, mande de novo". */
export const ultimoEnvioSchema = z.object({
  situacao: situacaoEnvioSchema,
  para: z.string(),
  enviadoEm: dataHoraSchema,
});

export const pedidoAEnviarSchema = z.object({
  id: uuidSchema,
  numero: z.number().int(),
  dataLancamento: dataSchema,
  empresaId: uuidSchema,
  empresaNome: z.string().nullable(),
  fornecedorId: uuidSchema,
  fornecedorNome: z.string().nullable(),
  /**
   * O e-mail do **cadastro** do fornecedor — o destinatário do lote.
   *
   * Não é a sugestão do diálogo de um pedido só (`sugestaoDoEnvio`), que prefere
   * o último envio: no lote o comprador não vê nem corrige endereço por
   * endereço, e o que ele vê na linha — e edita pelo atalho — tem de ser
   * exatamente para onde o e-mail vai.
   */
  fornecedorEmail: z.string().nullable(),
  valorTotal: z.string(),
  /** A trava da autorização (decisão 108), já resolvida pelo servidor. */
  liberado: z.boolean(),
  ultimoEnvio: ultimoEnvioSchema.nullable(),
  /** Quantos envios valendo o pedido já teve. Zero é "não enviado". */
  enviosValendo: z.number().int().nonnegative(),
});

export type PedidoAEnviar = z.infer<typeof pedidoAEnviarSchema>;

export const filaDeEnvioSchema = respostaListaSchema(pedidoAEnviarSchema);
export type FilaDeEnvio = z.infer<typeof filaDeEnvioSchema>;

/**
 * Por que a linha não se marca — ou `null`, quando se marca.
 *
 * A ordem é a do que o comprador resolve primeiro: sem aprovação não adianta
 * cadastrar e-mail, porque o pedido não sairia de qualquer forma. O servidor
 * recusa os dois casos por conta própria (`pedido_nao_aprovado`, e o contrato
 * do envio, que exige o destinatário); isto é para a tela não deixar marcar.
 */
export function motivoParaNaoEnviar(pedido: {
  liberado: boolean;
  fornecedorEmail: string | null;
}): string | null {
  if (!pedido.liberado) return 'Aguarda aprovação.';
  if (!pedido.fornecedorEmail) return 'O fornecedor não tem e-mail no cadastro.';
  return null;
}

/* -------------------------------------------------------------------------- */
/* Regras compartilhadas                                                      */
/* -------------------------------------------------------------------------- */

/** O assunto que a tela sugere. O fornecedor procura pelo número. */
export function assuntoPadraoDoEnvio(numero: number, empresa: string): string {
  return `Pedido de compra nº ${numero} — ${empresa}`;
}

/** O texto que a tela sugere; quem envia o ajusta. */
export function mensagemPadraoDoEnvio(numero: number, empresa: string): string {
  return [
    'Prezados,',
    '',
    `Segue em anexo o pedido de compra nº ${numero}.`,
    'Por favor, confirmem o recebimento e o prazo de entrega respondendo a este e-mail.',
    '',
    'Atenciosamente,',
    empresa,
  ].join('\n');
}

/**
 * Lê o campo "com cópia" da tela: endereços separados por vírgula, ponto e
 * vírgula ou quebra de linha — é como se cola de um cliente de e-mail.
 */
export function copiasDoTexto(texto: string): string[] {
  return texto
    .split(/[,;\n]/)
    .map((parte) => parte.trim().toLowerCase())
    .filter((parte) => parte !== '');
}
